// ============================================================
// CONDITION MOVEMENT - how far a combatant can get, given the
// state they are in.
//
// `conditionModifiers.ts` turns conditions into penalties on TESTS. This
// turns them into metres, which is a different question with different
// answers: Fatigue is −10 per level to characteristic tests and says
// nothing about how fast you walk, while Immobilised says nothing about
// tests at all and stops you dead.
//
// The rules that are explicit in docs/rules-spec.md are taken as read:
//
//   Immobilised   "Cannot move or Dodge"
//   Unconscious   "Cannot take any actions"
//   Leg Crippled  "Movement halved"
//   Pinned/Prone  "Cannot take actions except to Snap Fire or crawl"
//
// The two that are not - Fatigue, and what a GM means by writing
// "−10 to movement" - are settled below and marked as such.
//
// Pure. Zero React dependency.
// ============================================================
import { condType, condDuration, parseCustomPen } from './conditions';
import type { Movement } from './character/types';

/**
 * The custom-modifier scope that means MOVEMENT, and only movement.
 *
 * Its own scope rather than Agility, deliberately: a GM writing "−10 to
 * movement" is not saying the man has become clumsier, only slower, and
 * an Agility penalty would follow him into every Agility test he makes.
 *
 * The NUMBER keeps the meaning it has everywhere else in the app - one
 * −10 is one step - which for movement is one point of Agility Bonus,
 * since Deathwatch movement is AB / 2AB / 3AB / 6AB.
 */
export const MOVEMENT_SCOPE = 'movement';

/** One step of Agility Bonus, which is one metre of half move. */
const STEP = 1;

const NO_MOVEMENT: Movement = { half: 0, full: 0, charge: 0, run: 0 };

/**
 * Conditions under which nobody goes anywhere, and what to call them.
 *
 * IMMOBILISED HAS TWO SPELLINGS and both are real. `Immob` is what the
 * condition picker stores; `Immobilised`, spelled out, is what
 * `weaponEffects.ts` pushes when a weapon snares somebody - which is how
 * a PERSON comes by it, the picker offering it to vehicles only.
 * `conditions.ts` already carries both in `COND_BODY_MAP` and special-
 * cases the long one for colour; knowing only the short one meant a
 * snared combatant walked away as though nothing had happened.
 */
const STOPS: Record<string, string> = {
  Immob: 'Immobilised',
  Immobilised: 'Immobilised',
  Sn: 'Snared',
  U: 'Unconscious',
  Pa: 'Paralysed',
  Dy: 'Dying',
  K: 'Dead',
  S: 'Stunned',
};

export interface MovementUnderOptions {
  /**
   * Fatigue slows you down.
   *
   * OFF unless a table asks for it. RAW, each level is −10 to
   * characteristic TESTS, and movement derives from the characteristic
   * rather than from a test - so strictly it does not slow you. Most
   * tables rule that an exhausted man moves less, which is what this is
   * for. See `resolveFatigueSlowsMovement`.
   */
  fatigueSlows?: boolean;
}

export interface MovementUnder {
  /** What they can actually manage, in metres. */
  movement: Movement;
  /** Why it differs from the sheet, in the order the rules applied. */
  reasons: string[];
  /** They are not going anywhere at all. */
  stopped: boolean;
}

/** Base code, with a location-scoped critical kept whole (`Cr:Left Leg`). */
function baseOf(code: string): string {
  if (code.startsWith('Cr:') || code.startsWith('De:')) return code.slice(0, 2);
  return condType(code);
}

/** Scale every rate by the same factor, floored at nothing and rounded. */
function scale(movement: Movement, factor: number): Movement {
  return {
    half: Math.max(0, Math.round(movement.half * factor)),
    full: Math.max(0, Math.round(movement.full * factor)),
    charge: Math.max(0, Math.round(movement.charge * factor)),
    run: Math.max(0, Math.round(movement.run * factor)),
  };
}

/** The first condition that stops you outright, as its name; null when none does. */
function stoppedBy(conditions: readonly string[]): string | null {
  for (const code of conditions) {
    const stop = STOPS[baseOf(code)];
    if (stop) return stop;
  }
  return null;
}

type Step = { step: number; reason: string };

/** A custom modifier on movement: ten points a step. */
function penStep(code: string): Step | null {
  const pen = parseCustomPen(code);
  if (!pen?.scopes.includes(MOVEMENT_SCOPE) || !pen.value) return null;
  return { step: pen.value / 10, reason: `Movement ${pen.value > 0 ? '+' : '−'}${Math.abs(pen.value)}` };
}

/** Fatigue, a step down per level - only when the table rules it slows. */
function fatigueStep(code: string, options: MovementUnderOptions): Step | null {
  if (!options.fatigueSlows || condType(code) !== 'F') return null;
  const level = condDuration(code) || 1;
  return { step: -level, reason: `Fatigue (${level})` };
}

/**
 * Steps from the modifiers written against movement and, when the table
 * rules it, Fatigue. Summed in condition order (the float sum depends on
 * it), the reasons alongside.
 */
function movementSteps(conditions: readonly string[], options: MovementUnderOptions) {
  const parts = conditions
    .flatMap((code) => [penStep(code), fatigueStep(code, options)])
    .filter((p): p is Step => p !== null);
  return { steps: parts.reduce((sum, p) => sum + p.step, 0), reasons: parts.map((p) => p.reason) };
}

/**
 * The block after its steps, or null when they slow it to a stop. A step
 * is one point of Agility Bonus, which IS the half move - so the whole
 * block scales together whether or not it came from a characteristic. A
 * vehicle with odd rates scales in proportion.
 */
function stepped(base: Movement, steps: number): Movement | null {
  if (steps === 0 || base.half <= 0) return base;
  const half = base.half + steps * STEP;
  return half <= 0 ? null : scale(base, half / base.half);
}

/** The postures that cap you at a crawl - at most a Half Move. Pinned: rules-spec.md's "Snap Fire or crawl" (v8.198.4). */
const CRAWLS: readonly [string, string][] = [
  ['P', 'Prone - a crawl, at most a Half Move'],
  ['Pn', 'Pinned - a crawl, at most a Half Move'],
];

/** Halve for a ruined leg, then cap at a crawl for each crawling posture, noting each. */
function hobbled(movement: Movement, conditions: readonly string[], reasons: string[]): Movement {
  let out = movement;
  const leg = conditions.find((c) => /^Cr:.*leg/i.test(c));
  if (leg) {
    reasons.push(`Crippled ${leg.slice(3).replace(/\b\w/g, (ch) => ch.toUpperCase())} - movement halved`);
    out = scale(out, 0.5);
  }
  for (const [code, reason] of CRAWLS) {
    if (!conditions.some((c) => baseOf(c) === code)) continue;
    reasons.push(reason);
    out = { half: out.half, full: out.half, charge: out.half, run: out.half };
  }
  return out;
}

/**
 * How far this combatant can get, given what is wrong with them.
 *
 * Applied in the order the rules stack: what stops you, then what scales
 * you, then what caps you. A cap last, because crawling is a ceiling on
 * whatever you had left rather than a rate of its own.
 */
export function movementUnder(
  sheet: Movement | undefined,
  conditions: string[] = [],
  options: MovementUnderOptions = {},
): MovementUnder {
  const stop = stoppedBy(conditions);
  if (stop) return { movement: { ...NO_MOVEMENT }, reasons: [`${stop} - cannot move`], stopped: true };
  const { steps, reasons } = movementSteps(conditions, options);
  const scaled = stepped(sheet ? { ...sheet } : { ...NO_MOVEMENT }, steps);
  if (!scaled) return { movement: { ...NO_MOVEMENT }, reasons: [...reasons, 'slowed to a stop'], stopped: true };
  const movement = hobbled(scaled, conditions, reasons);
  return { movement, reasons, stopped: movement.run <= 0 };
}
